import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  Checkbox,
  FormControl,
  FormField,
  FormItem,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@egaranti/components";

import React from "react";
import { useTranslation } from "react-i18next";

import {
  AlignCenter,
  Calendar,
  CheckSquare,
  ChevronDown,
  Circle,
  GripVerticalIcon,
  Hash,
  PlusIcon,
  TrashIcon,
  Type,
} from "lucide-react";

const FIELD_TYPES = [
  {
    value: "text",
    label: "Text",
    icon: <Type className="h-4 w-4" />,
  },
  {
    value: "number",
    label: "Number",
    icon: <Hash className="h-4 w-4" />,
  },
  {
    value: "textarea",
    label: "Textarea",
    icon: <AlignCenter className="h-4 w-4" />,
  },
  {
    value: "select",
    label: "Select",
    icon: <ChevronDown className="h-4 w-4" />,
  },
  {
    value: "radio",
    label: "Radio",
    icon: <Circle className="h-4 w-4" />,
  },
  {
    value: "checkbox",
    label: "Checkbox",
    icon: <CheckSquare className="h-4 w-4" />,
  },
  {
    value: "date",
    label: "Date",
    icon: <Calendar className="h-4 w-4" />,
  },
];

const SortableFormField = ({ id, index, field, form, onRemove }) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const addOption = () => {
    const currentFields = form.getValues("formFields") || [];
    const newFields = [...currentFields];
    newFields[index].options = [...(newFields[index].options || []), ""];
    form.setValue("formFields", newFields);
  };

  const removeOption = (optionIndex) => {
    const currentFields = form.getValues("formFields") || [];
    const newFields = [...currentFields];
    newFields[index].options = newFields[index].options.filter(
      (_, i) => i !== optionIndex,
    );
    form.setValue("formFields", newFields);
  };

  const fieldType = form.watch(`formFields.${index}.type`);
  const hasOptions = fieldType === "select" || fieldType === "radio";

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="relative">
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          {...listeners}
          className="cursor-grab p-1 hover:cursor-grabbing"
          type="button"
        >
          <GripVerticalIcon className="h-5 w-5 text-gray-400" />
        </Button>
        <div className="flex-1 rounded-lg border bg-white p-4 hover:border-gray-300">
          <div className="flex items-start gap-4">
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name={`formFields.${index}.label`}
                  render={({ field }) => (
                    <FormItem>
                      <Label>
                        {t(
                          "settingsPage.productRegisterActivation.formBuilder.fieldName",
                          "Alan Adı / Soru",
                        )}
                      </Label>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t(
                            "settingsPage.productRegisterActivation.formBuilder.fieldNamePlaceholder",
                            "Örn: Telefon numaranız",
                          )}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`formFields.${index}.type`}
                  render={({ field: fieldProps }) => (
                    <FormItem>
                      <Label>
                        {t(
                          "settingsPage.productRegisterActivation.formBuilder.fieldType",
                          "Alan Tipi",
                        )}
                      </Label>
                      <Select
                        onValueChange={fieldProps.onChange}
                        defaultValue={fieldProps.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <div className="flex items-center gap-2">
                              {
                                FIELD_TYPES.find(
                                  (type) => type.value === fieldProps.value,
                                )?.icon
                              }
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {FIELD_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-2">
                                <span>
                                  {t(
                                    `settingsPage.productRegisterActivation.formBuilder.fieldTypes.${type.value}`,
                                    type.label,
                                  )}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>

              {hasOptions && (
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <Label>
                      {t(
                        "settingsPage.productRegisterActivation.formBuilder.options",
                        "Seçenekler",
                      )}
                    </Label>
                    <Button
                      type="button"
                      variant="secondaryGray"
                      size="sm"
                      onClick={addOption}
                    >
                      <PlusIcon className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {form
                      .watch(`formFields.${index}.options`)
                      ?.map((_, optionIndex) => (
                        <div
                          key={optionIndex}
                          className="flex items-center gap-2"
                        >
                          <FormField
                            control={form.control}
                            name={`formFields.${index}.options.${optionIndex}`}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder={`${t(
                                  "settingsPage.productRegisterActivation.formBuilder.optionPlaceholder",
                                  "Seçenek",
                                )} ${optionIndex + 1}`}
                              />
                            )}
                          />
                          <Button
                            type="button"
                            variant="secondaryGray"
                            size="sm"
                            onClick={() => removeOption(optionIndex)}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              <div />
            </div>
            <div className="flex items-center justify-end gap-4">
              <FormField
                control={form.control}
                name={`formFields.${index}.required`}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <Label>
                      {t(
                        "settingsPage.productRegisterActivation.formBuilder.requiredField",
                        "Zorunlu alan",
                      )}
                    </Label>
                  </FormItem>
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onRemove}
                className="text-gray-400 hover:bg-red-50 hover:text-red-500"
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SortableFormField;
